/**
 * Employer Job Posting API Service
 *
 * Backs the "Зар нийтлэх" wizard and the employer's own ad list. An ad is live in the
 * public feed the moment the wizard finishes — nothing reviews it first. The platform can
 * take a bad one down afterwards, which is the only reason an ad here is ever not posted.
 *
 * Owns the translation between the wizard's Mongolian option labels and the values
 * the API stores, so the page stays presentational and there is one place to look
 * when a label changes.
 */

import http from '../client';
import { ENDPOINTS } from '../config';
import { SCHEDULE_LABEL_TO_CODE } from '../../data/workPreferences';

/**
 * Request/response tracing for the two calls the company registration hangs on.
 *
 * Between them these decide whether an employer is told to register a company —
 * possibly one they already have. Every failure used to arrive at the UI as a bare
 * `{ success: false, error: "<message>" }`, which is the same shape whether the token
 * expired, the server rejected the registration number, or the request never left the
 * browser. There was nothing to tell them apart by, so the cause could only be guessed.
 *
 * So both directions are recorded: what went out, and what came back. A thrown
 * ApiError is flattened into the same result shape as before, but keeps its HTTP
 * status and response body instead of discarding them.
 *
 * Failures are logged in every environment, because the ones worth catching happen on
 * the deployed site. The successful round trips are dev-only noise.
 */
const traced = async (label, request, run) => {
  const dev = process.env.NODE_ENV === 'development';
  const started = Date.now();
  if (dev) console.info(`[employer] → ${label}`, request ?? '');

  try {
    const res = await run();
    if (dev) {
      console.info(`[employer] ← ${label} (${Date.now() - started}ms)`, {
        success: res?.success,
        status: res?.status,
        data: res?.data,
      });
    }
    return res;
  } catch (error) {
    console.error(`[employer] ✕ ${label} (${Date.now() - started}ms)`, {
      message: error?.message,
      // 0 means the request never got a reply — offline, CORS, or a timeout. That is a
      // different problem from any status the server actually sent back.
      status: error?.status ?? 0,
      body: error?.data ?? null,
      request,
    });
    return {
      success: false,
      error: error?.message || 'Сүлжээний алдаа гарлаа.',
      status: error?.status ?? 0,
      data: error?.data ?? null,
    };
  }
};

/**
 * "Ажлын цаг" -> employment type.
 *
 * Derived from the candidate-side list rather than repeated here: the whole point of these
 * three codes is that what a person wants and what an ad offers are the same token, and two
 * copies of the mapping is how that stops being true. See src/data/workPreferences.js.
 */
const WORK_SCHEDULE = SCHEDULE_LABEL_TO_CODE;

/** "Туршлага" -> experience level. */
const EXPERIENCE_LEVEL = {
  'Шаардлагагүй': 'NONE',
  '1-3 жил': 'Y1_3',
  '3+ жил': 'Y3_PLUS',
};

/** The map picker yields [lat, lng]; the API stores the pair as text. */
const formatCoordinates = (coordinates) =>
  Array.isArray(coordinates) && coordinates.length === 2
    ? `${coordinates[0]},${coordinates[1]}`
    : null;

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Escapes a block of employer-typed plain text and turns it into safe list HTML.
 *
 * introductionText/requirementText render via dangerouslySetInnerHTML on the public
 * job page (see JobDetail.jsx), whose CSS forces whitespace-normal — a raw newline
 * would visually collapse. Wrapping each non-empty line in <li> instead of joining
 * with <br> also matches what JobPreviewDrawer.jsx's extractListItems() already looks
 * for (a real <li> per point) and what JobDetail's own CSS is pre-styled for
 * ([&_ul]:!list-disc etc.) — so one shape here satisfies every existing reader.
 *
 * Blank/whitespace-only input returns null, matching the salaryText null-on-empty
 * convention below rather than sending "" or "<ul></ul>" to the server.
 */
const textToListHtml = (text) => {
  const lines = (text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;
  return `<ul>${lines.map((l) => `<li>${escapeHtml(l)}</li>`).join("")}</ul>`;
};

/**
 * Wizard form state -> API payload.
 *
 * The salary band is sent as the label the employer picked ("3 - 4 сая") rather than
 * parsed numbers: the server owns what a band is worth, so the two can't drift.
 *
 * @param {Object} form   PostJob form state
 * @param {string} regNo  registration number entered in step 5
 * @param {number} industryId  sector's backend industry (from WIZARD_SECTORS)
 */
export const toJobPayload = (
  form,
  regNo,
  industryId,
  { platformStaff = false, postAsOwnCompany = false } = {}
) => ({
  industryId,
  jobGroupId: form.jobGroupId ?? null,
  jobTitleId: form.jobTitleId ?? null,
  // Only meaningful when no catalog position was picked.
  customTitle: form.jobTitleId ? null : (form.role || null),

  title: form.title?.trim(),
  introductionText: textToListHtml(form.introductionText),
  requirementText: textToListHtml(form.requirementText),
  workSchedule: WORK_SCHEDULE[form.schedule] || null,
  experienceLevel: EXPERIENCE_LEVEL[form.experience] || null,
  salaryText: form.salaryNegotiable ? null : form.salary,
  salaryNegotiable: !!form.salaryNegotiable,

  region: form.region || null,
  district: form.district || null,
  address: form.address || null,
  coordinates: formatCoordinates(form.coordinates),

  // Platform staff post under the company their token already proves they belong to,
  // so these are omitted — sending them would be asking the server to take the
  // poster's word for which company this is. An admin who picked "Манай байгууллага" is
  // in exactly that position: the server reads the company off their own account.
  regNo: platformStaff || postAsOwnCompany ? null : (regNo || '').trim(),
  companyName: platformStaff || postAsOwnCompany ? null : form.companyName?.trim(),
  contactPhone: form.contactPhone?.trim(),

  // Only an admin is offered the choice, and the server only believes it from one: it
  // selects between two companies it already knows the caller may use, and names neither.
  postAsOwnCompany,
});

/**
 * Who the signed-in person posts as.
 *
 * `platformStaff` means their phone is already an HR user here, so the ad files under
 * their own company and the wizard has no reason to ask for a registration number.
 * `publishesDirectly` is false when their company requires postings to be approved.
 *
 * @returns {Promise<{success: boolean, data?: {platformStaff: boolean, publishesDirectly: boolean, companyId: ?number, companyName: ?string}, error?: string}>}
 */
export const getEmployerIdentity = async () =>
  traced("GET /diil/employer/me", null, () => http.get(ENDPOINTS.EMPLOYER.ME));

/**
 * Find or create the employer's company by registration number.
 * Called after the ebarimt lookup so the ad attaches to a real company.
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const resolveCompany = async ({ regNo, companyName, phone, logoUrl }) => {
  // logoUrl is only ever a URL the directory endpoint just returned, and the server
  // refuses any that is not — it is a suggestion being accepted, not a free-form field.
  const payload = { regNo, companyName, phone, logoUrl };
  return traced("POST /diil/employer/company/resolve", payload, () =>
    http.post(ENDPOINTS.EMPLOYER.RESOLVE_COMPANY, payload)
  );
};

/**
 * What registering this company would do, before the employer commits:
 * CREATE (new), JOIN (someone already registered it — they join as a manager),
 * ALREADY_MEMBER, or TAKEN (a same-name company that belongs to others; `message` says why).
 * Writes nothing.
 *
 * @returns {Promise<{success: boolean, data?: {outcome: string, companyId?: number, companyName?: string, message?: string}}>}
 */
export const previewCompanyRegistration = async ({ regNo, companyName }) =>
  traced("GET /diil/employer/company/register-preview", { regNo, companyName }, () =>
    http.get(ENDPOINTS.EMPLOYER.REGISTER_PREVIEW, { regNo, companyName })
  );

/**
 * Which self-serve company a contact number already belongs to.
 *
 * Admin-only server-side; anyone else gets a 403, which arrives here as
 * `{ success: false }` and is treated the same as "not found" — the wizard simply asks for
 * the company name instead. An empty `{}` body means the number is new.
 *
 * @param {string} phone
 * @returns {Promise<{success: boolean, data?: {companyId?: number, name?: string}}>}
 */
export const lookupEmployerCompanyByPhone = async (phone) =>
  traced("GET /diil/employer/company/by-phone", { phone }, () =>
    http.get(ENDPOINTS.EMPLOYER.COMPANY_BY_PHONE(phone))
  );

/**
 * Texts the company's registered contact that applicants have come in, with a link to
 * their own public page. Admin-only server-side — for a business an admin posted for
 * that never logs into Diil itself.
 *
 * @param {number|string} companyId
 * @returns {Promise<{success: boolean, data?: {sent: boolean, phone: string}, error?: string}>}
 */
export const notifyCompanyApplicants = async (companyId) =>
  traced("POST /diil/employer/company/:id/notify-applicants", { companyId }, () =>
    http.post(ENDPOINTS.EMPLOYER.NOTIFY_COMPANY(companyId), {})
  );

/**
 * Fill in the employer's own company page.
 *
 * Send only the fields being edited — anything omitted keeps its stored value, so a
 * one-section save never has to resend the rest. `companyAdvantages` / `companyBenefits`
 * go as arrays of strings; the server stores them as the JSON strings the company page
 * already knows how to read, and echoes them back in that form.
 *
 * @param {{companyDescription?: string, companyAdvantages?: string[], companyBenefits?: string[]}} payload
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const updateCompanyProfile = async (payload) =>
  traced("PUT /diil/employer/company", payload, () =>
    http.put(ENDPOINTS.EMPLOYER.COMPANY_PROFILE, payload)
  );

/**
 * Replace the company logo. Multipart — the server mints the URL, so the address the
 * public page renders is never one a client chose.
 * @param {File} file
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const uploadCompanyPhoto = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    return await http.postFormData(ENDPOINTS.EMPLOYER.COMPANY_PHOTO, formData);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Replace the logo of a company the caller doesn't belong to — an admin who posted for
 * it, same authorization shape as notifyCompanyApplicants. Same server-minted-URL
 * reasoning as uploadCompanyPhoto; this only differs by carrying the companyId a token
 * can't resolve on its own.
 * @param {string|number} companyId
 * @param {File} file
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const uploadCompanyPhotoById = async (companyId, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    return await http.postFormData(ENDPOINTS.EMPLOYER.COMPANY_PHOTO_BY_ID(companyId), formData);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Publish an ad. It is live as soon as this returns; the response carries the company it
 * was filed under, which is the only place an admin learns that — they have none of their own.
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const createJob = async (payload) => {
  try {
    return await http.post(ENDPOINTS.EMPLOYER.JOBS, payload);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * The employer's own ads, any approval status. Backs "Миний зар".
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getMyJobs = async () => {
  try {
    return await http.get(ENDPOINTS.EMPLOYER.JOBS);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Edit an ad. It stays live, and one that was taken down goes back up. */
export const updateJob = async (id, payload) => {
  try {
    return await http.put(ENDPOINTS.EMPLOYER.JOB(id), payload);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Withdraw an ad. Soft delete — applicants already attached to it are kept. */
export const deleteJob = async (id) => {
  try {
    return await http.delete(ENDPOINTS.EMPLOYER.JOB(id));
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Applicants on one ad. */
export const getJobApplicants = async (id) => {
  try {
    return await http.get(ENDPOINTS.EMPLOYER.JOB_APPLICANTS(id));
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Marks where the employer has got to with an applicant: called / scheduled / accepted /
 * rejected, or null to clear it.
 *
 * Goes to the server rather than staying on the device, because the candidate's own
 * "Миний өргөдлүүд" shows this same decision back to them — a mark kept locally told them
 * nothing, and disappeared the moment the employer opened Diil somewhere else.
 *
 * @param {number} jobId the ad the applicant applied to
 * @param {number} applicantId the applicant's `id` from getJobApplicants
 * @param {?string} status one of APPLICANT_STATUSES' ids, or null
 */
export const setApplicantStatus = async (jobId, applicantId, status) =>
  traced("PUT /diil/employer/jobs/:id/applicants/:id/status", { jobId, applicantId, status }, () =>
    http.put(ENDPOINTS.EMPLOYER.APPLICANT_STATUS(jobId, applicantId), { status: status ?? null })
  );

/**
 * One applicant's AI interview result — the score and the summary the interviewer wrote.
 *
 * The interview itself runs in aichatbot, but its verdict is written back onto the
 * assessment_player row, which is what this reads. The conversation itself is not
 * available here: it lives in aichatbot behind the candidate's own token, so an employer
 * sees what the interview concluded, not what was said.
 *
 * Answers with an array — the row plus any merged duplicates — and with an empty one for
 * an id that has no row. A candidate who never interviewed simply has no chat fields, so
 * "no interview" and "not found" collapse into the same null here, which is what the UI
 * needs either way.
 *
 * @param {number} assessmentPlayerId the applicant's `id` from getJobApplicants
 * @returns {Promise<{percentage: ?number, summary: ?string, completeType: ?string}|null>}
 */
export const getApplicantInterview = async (assessmentPlayerId) => {
  try {
    const res = await http.get(ENDPOINTS.EMPLOYER.APPLICANT_INTERVIEW(assessmentPlayerId));
    const row = (Array.isArray(res?.data) ? res.data : [])[0];
    if (!row) return null;

    const percentage = Number.isFinite(row.chatPercentage) ? row.chatPercentage : null;
    const summary = (row.chatSummary || '').trim() || null;
    if (percentage === null && !summary) return null;

    return { percentage, summary, completeType: row.completeType || null };
  } catch {
    // One applicant's missing interview must not take the applicants list down with it —
    // the row still renders, just without a score.
    return null;
  }
};

/**
 * One applicant's CV. Routed through the ad, so it is readable only by the employer
 * who owns the posting that person applied to.
 */
export const getApplicantCv = async (jobId, candidateId) => {
  try {
    return await http.get(ENDPOINTS.EMPLOYER.APPLICANT_CV(jobId, candidateId));
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * The CV-bank rows for every applicant on one ad, in one request.
 *
 * The same `DiilAdminCvRowDto` shape {@code adminCv.getAdminCvRowsByCandidates} returns —
 * `CvRowCard` reads either without knowing which it got — but reached through the ad, so
 * an ordinary employer gets it for their own applicants. Anyone with no active CV is
 * simply absent from the answer.
 */
export const getApplicantCvRows = async (jobId) => {
  try {
    return await http.get(ENDPOINTS.EMPLOYER.APPLICANT_CV_ROWS(jobId));
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * CvResponseDto -> the flat shape the CV panel renders.
 * Kept here next to the API call so the panel stays a pure view.
 *
 * `full` is for the admin CV bank alone (AdminCvDetail). It keeps every field the CV
 * carries rather than the handful a profile shows — industry, salary and place of each job,
 * its achievements, GPA, skill level, certificate expiry and so on — by folding them into
 * the same `sub` / `period` / `detail` slots the sections already draw, so the layout is
 * untouched. Every other caller (own profile, public profile, employer applicant sheet)
 * passes nothing and gets exactly the shape it always has.
 */
export const toCvView = (cv, applicant, { full = false } = {}) => {
  if (!cv) return null;
  // Admin-only extras: a "Label: value" line per present fact, joined under the entry's
  // own text. Empty in the default mode, so nothing changes for anyone else.
  const facts = (pairs) =>
    full ? pairs.filter(([, v]) => v != null && String(v).trim() !== "").map(([k, v]) => `${k}: ${v}`).join("\n") : "";
  const withFacts = (text, pairs) => [text, facts(pairs)].filter(Boolean).join("\n\n");
  const place = (e) => [e.city, e.country].filter(Boolean).join(", ");
  // "2020 — 2023", "2020 — одоог хүртэл", or just "2023" for a row saved before start
  // dates were stored at all. An entry with neither date gets nothing rather than a bare
  // "одоог хүртэл", which would claim someone still works somewhere on no evidence.
  const period = (from, to) => {
    if (!from && !to) return "";
    return [from, to || "одоог хүртэл"].filter(Boolean).join(" — ");
  };

  // Newest first, for both chronologies. The API hands these back in the order they were
  // entered, which is neither — and every reader of this shape treats [0] as the current
  // job or the latest school (the line under the name on a profile is exactly that), so
  // the sort belongs here rather than in each of them.
  //
  // A row still running (a start date, no end) outranks every finished one; after that the
  // later end date wins, then the later start. Dates are partial strings ("2020",
  // "2026-07-06") and are compared as text, which orders them correctly because both
  // formats lead with the year. A row with no dates at all sorts last.
  const recency = (e) => (e.endDate ? e.endDate : e.startDate ? "9999" : "");
  const newestFirst = (a, b) =>
    recency(b).localeCompare(recency(a)) || String(b.startDate || "").localeCompare(String(a.startDate || ""));
  const jobs = [...(cv.workExperience || [])].sort(newestFirst);
  const schools = [...(cv.education || [])].sort(newestFirst);

  return {
    position: jobs[0]?.jobTitle || "",
    location: [jobs[0]?.city, jobs[0]?.country].filter(Boolean).join(", "),
    email: cv.candidate?.email || applicant?.email || "",
    // One line under the name; the paragraph is `summary`. See CandidateCv.bio.
    bio: cv.bio || "",
    summary: cv.summary || "",
    // Carried through so ProfileDetails can say when the paragraph was last written. Not
    // lastModifiedDate, which moves on any edit to any section — see CandidateCv.
    summaryUpdatedAt: cv.summaryUpdatedAt || null,

    // What this person wants from a job. Salary is on the CV, the rest is on the candidate —
    // see useWorkPreferences for why that split exists and why callers never have to care.
    //
    // Which of these a viewer actually receives is the API's decision, not this mapper's:
    // the public profile shape carries only `activelyLooking`, so on someone else's page the
    // salary is simply absent and its line does not render.
    minSalary: cv.minSalary ?? null,
    maxSalary: cv.maxSalary ?? null,
    preferredSectors: cv.candidate?.preferredSectors ?? null,
    preferredEmploymentTypes: cv.candidate?.preferredEmploymentTypes ?? null,
    // Days, for hourly and shift candidates — what the form now asks for. The hours figure
    // is still carried for CVs the AI interview filled in before it was dropped.
    daysPerWeek: cv.candidate?.daysPerWeek ?? null,
    availableHoursPerWeek: cv.candidate?.availableHoursPerWeek ?? null,
    canStartImmediately: cv.candidate?.canStartImmediately ?? null,
    // Also arrives top-level on the public profile shape, which has no `candidate` object.
    activelyLooking: cv.candidate?.activelyLooking ?? cv.activelyLooking ?? null,

    experience: jobs.map((e) => ({
      role: e.jobTitle,
      org: e.companyName,
      period: period(e.startDate, e.endDate),
      detail: full
        ? withFacts(e.description || "", [
          ["Амжилт", e.achievements],
          ["Салбар", e.industry],
          ["Цалин", e.salary],
          ["Хугацаа", e.duration],
          ["Байршил", place(e)],
        ])
        : e.description || e.achievements || "",
    })),
    education: schools.map((e) => ({
      school: e.institutionName,
      degree: [e.degree, e.fieldOfStudy].filter(Boolean).join(", "),
      period: period(e.startDate, e.endDate),
      // Same field experience has carried all along. It was collected and stored, then
      // dropped here — so what anyone wrote about their studies reached no screen.
      detail: withFacts(e.description || "", [
        ["Голч дүн", e.gpa],
        ["Байршил", place(e)],
      ]),
    })),
    // A skill stays one chip; in full mode its level and years go in brackets after it.
    skills: (cv.skills || []).map((s) => {
      if (!s.skillName) return null;
      const more = full
        ? [s.proficiencyLevel, s.yearsOfExperience != null && s.yearsOfExperience !== "" ? `${s.yearsOfExperience} жил` : null, s.category]
          .filter(Boolean).join(", ")
        : "";
      return more ? `${s.skillName} (${more})` : s.skillName;
    }).filter(Boolean),
    languages: (cv.languages || []).map((l) => ({
      name: l.languageName,
      level: l.proficiencyLevel || "",
    })),
    extras: [
      ...(cv.certificates || []).map((c) => c.name),
      ...(cv.hobbies || []).map((h) => h.hobbyName || h.name),
    ].filter(Boolean),

    // The rest of the CV. Absent from the public-profile endpoint by design (see
    // PublicCandidateProfileDto), so on someone else's page these are simply empty and the
    // sections do not render — the same mapper serves both, and the API decides what a
    // viewer is allowed to see rather than the client remembering to hide things.
    hobbies: (cv.hobbies || []).map((h) => h.hobbyName || h.name).filter(Boolean),
    certificates: (cv.certificates || []).map((c) => ({
      id: c.id,
      name: c.name,
      sub: c.issuer || "",
      period: full && c.expiryDate ? [c.issueDate, c.expiryDate].filter(Boolean).join(" — ") : c.issueDate || "",
      url: c.credentialUrl || "",
      ...(full && { detail: facts([["Дугаар", c.credentialId]]) }),
    })).filter((c) => c.name),
    projects: (cv.projects || []).map((p) => ({
      id: p.id,
      name: p.name,
      period: period(p.startDate, p.endDate),
      detail: p.description || "",
      url: p.url || "",
    })).filter((p) => p.name),
    publications: (cv.publications || []).map((p) => ({
      id: p.id,
      title: p.title,
      url: p.url || "",
      ...(full && {
        sub: p.publisher || "",
        period: p.publicationDate || "",
        detail: p.description || "",
      }),
    })).filter((p) => p.title),
    // The server's field is `awardDate`; the older names are kept for the default mode,
    // which this change leaves alone.
    awards: (cv.awards || []).map((a) => ({
      id: a.id,
      name: a.name,
      sub: a.issuer || "",
      period: (full && a.awardDate) || a.dateReceived || a.issueDate || "",
      ...(full && { detail: a.description || "" }),
    })).filter((a) => a.name),
    volunteers: (cv.volunteers || []).map((v) => ({
      id: v.id,
      role: v.role || v.position || "",
      org: v.organizationName || v.organization || "",
      period: period(v.startDate, v.endDate),
      detail: v.description || "",
    })).filter((v) => v.role || v.org),
    // The server names the organisation `organization`; the full mode reads it so the
    // admin sees memberships at all.
    memberships: (cv.memberships || []).map((m) => ({
      id: m.id,
      name: m.organizationName || m.name || (full && m.organization) || "",
      sub: m.role || "",
      period: period(m.startDate, m.endDate),
    })).filter((m) => m.name),
  };
};

const employerJobsService = {
  toJobPayload,
  getEmployerIdentity,
  resolveCompany,
  updateCompanyProfile,
  uploadCompanyPhoto,
  uploadCompanyPhotoById,
  createJob,
  getMyJobs,
  updateJob,
  deleteJob,
  getJobApplicants,
  setApplicantStatus,
  getApplicantInterview,
  getApplicantCv,
  getApplicantCvRows,
  toCvView,
};

export default employerJobsService;
